import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { BackButton } from 'components/UnvUI'
import { Oval } from 'react-loader-spinner'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'

import { fullEmployee, patchSection, uploadEmployeeFile } from 'service/EmployeeWizardService'

// Wave Final (P1-I4): label-литералы перенесены в i18n. Поле labelFb —
// fallback на русском, чтобы UI не ломался при отсутствии перевода в JSON.
// Ключи: hr.cardTab.* (выносятся в common.json переводчиком-носителем).
const TABS = [
  { key: 'personal',   labelKey: 'hr.cardTab.personal',    labelFb: 'Личное',        fields: ['last_name', 'first_name', 'middle_name', 'birth_date', 'gender', 'phone', 'email'] },
  { key: 'passport',   labelKey: 'hr.cardTab.passport',    labelFb: 'Паспорт',       fields: ['passport_series', 'passport_number', 'passport_issued_by', 'passport_issued_date', 'inn'] },
  { key: 'education',  labelKey: 'hr.cardTab.education',   labelFb: 'Образование',   fields: ['edu_institution', 'edu_speciality', 'edu_degree', 'edu_year'] },
  { key: 'military',   labelKey: 'hr.cardTab.military',    labelFb: 'Военный учёт',  fields: ['military_status', 'military_rank', 'military_specialty'] },
  { key: 'family',     labelKey: 'hr.cardTab.family',      labelFb: 'Семья',         fields: ['marital_status', 'children_count', 'spouse_name'] },
  { key: 'address',    labelKey: 'hr.cardTab.address',     labelFb: 'Адрес',         fields: ['address_reg', 'address_actual'] },
  { key: 'languages',  labelKey: 'hr.cardTab.languages',   labelFb: 'Языки',         fields: ['languages'] },
  { key: 'awards',     labelKey: 'hr.cardTab.awards',      labelFb: 'Награды',       fields: ['awards'] },
  { key: 'experience', labelKey: 'hr.cardTab.experience',  labelFb: 'Стаж',          fields: ['experience_total', 'experience_pedagogical', 'experience_at_chu'] },
  { key: 'training',   labelKey: 'hr.cardTab.training',    labelFb: 'Обучение',      fields: ['training_courses'] },
  { key: 'assignments',labelKey: 'hr.cardTab.assignments', labelFb: 'Назначения',    fields: ['assignments'] },
  { key: 'vacations',  labelKey: 'hr.cardTab.vacations',   labelFb: 'Отпуска',       fields: ['vacations'] },
  { key: 'files',      labelKey: 'hr.cardTab.files',       labelFb: 'Файлы',         fields: ['files'] },
  { key: 'history',    labelKey: 'hr.cardTab.history',     labelFb: 'История',       fields: ['history'] },
]

const fmt = (v) => {
  if (v == null || v === '') return '—'
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return dayjs(v).format('DD.MM.YYYY')
  return String(v)
}

const EmployeeCardPage = () => {
  const { t } = useTranslation('common')

  const { id } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState(0)
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [editValues, setEditValues] = useState({})
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await fullEmployee(id)
      setEmployee(data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось загрузить карточку сотрудника')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const currentTab = TABS[tab]

  const sectionData = useMemo(() => {
    if (!employee) return {}
    return employee[currentTab.key] || employee
  }, [employee, currentTab])

  const startEdit = () => {
    const initial = {}
    currentTab.fields.forEach((f) => {
      initial[f] = sectionData?.[f] ?? ''
    })
    setEditValues(initial)
    setEditMode(true)
  }

  const saveEdit = async () => {
    setBusy(true)
    try {
      await patchSection(id, currentTab.key, editValues)
      Swal.fire({ icon: 'success', title: 'Сохранено', timer: 1500, showConfirmButton: false })
      setEditMode(false)
      await load()
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Ошибка', text: e?.response?.data?.detail || 'Не удалось сохранить' })
    } finally {
      setBusy(false)
    }
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('title', file.name)
    setBusy(true)
    try {
      await uploadEmployeeFile(id, fd)
      await load()
      Swal.fire({ icon: 'success', title: 'Файл загружен', timer: 1200, showConfirmButton: false })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Ошибка', text: err?.response?.data?.detail || 'Не удалось загрузить файл' })
    } finally {
      setBusy(false)
      e.target.value = ''
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}>
        <Oval height={48} width={48} color="#FF7A00" secondaryColor="#ffe0c2" />
      </Box>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <BackButton style={{ marginTop: 16 }} />
      </Container>
    )
  }

  const renderContent = () => {
    if (currentTab.key === 'files') {
      const files = employee?.files || []
      return (
        <Stack spacing={2}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
            Загрузить файл
            <input type="file" hidden onChange={handleFile} />
          </Button>
          {files.length === 0 ? (
            <Typography color="text.secondary">Нет файлов</Typography>
          ) : (
            <Stack spacing={1}>
              {files.map((f) => (
                <Paper key={f.id} sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{f.title || f.name || `Файл #${f.id}`}</span>
                  {f.url && (
                    <a href={f.url} target="_blank" rel="noreferrer">
                      {t('common.download')}
                    </a>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )
    }

    if (currentTab.key === 'history') {
      const history = employee?.history || []
      return history.length === 0 ? (
        <Typography color="text.secondary">История пуста</Typography>
      ) : (
        <Stack spacing={1}>
          {history.map((h, i) => (
            <Paper key={h.id || i} sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {fmt(h.created_at || h.date)} — {h.actor_name || h.actor || ''}
              </Typography>
              <Typography>{h.action || h.event || h.message || JSON.stringify(h)}</Typography>
            </Paper>
          ))}
        </Stack>
      )
    }

    return (
      <Grid container spacing={2}>
        {currentTab.fields.map((f) => (
          <Grid item xs={12} sm={6} key={f}>
            {editMode ? (
              <TextField
                fullWidth
                label={f}
                value={editValues[f] ?? ''}
                onChange={(e) => setEditValues({ ...editValues, [f]: e.target.value })}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">{f}</Typography>
                <Typography>{fmt(sectionData?.[f])}</Typography>
              </Box>
            )}
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <BackButton variant="icon" />

        <Typography variant="h5" fontWeight={700}>
          {employee?.full_name ||
            `${employee?.last_name || ''} ${employee?.first_name || ''}`.trim() ||
            `Сотрудник #${id}`}
        </Typography>
      </Stack>

      <Paper sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v)
            setEditMode(false)
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} label={t(tab.labelKey, tab.labelFb)} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {!['files', 'history'].includes(currentTab.key) && (
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
              {!editMode ? (
                <Button startIcon={<EditIcon />} onClick={startEdit}>
                  Редактировать
                </Button>
              ) : (
                <>
                  <Button startIcon={<CloseIcon />} onClick={() => setEditMode(false)} disabled={busy}>
                    Отмена
                  </Button>
                  <Button
                    startIcon={busy ? <Oval height={14} width={14} color="#fff" secondaryColor="#ffe0c2" /> : <SaveIcon />}
                    variant="contained"
                    onClick={saveEdit}
                    disabled={busy}
                    sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
                  >
                    Сохранить
                  </Button>
                </>
              )}
            </Stack>
          )}

          {renderContent()}
        </Box>
      </Paper>
    </Container>
  )
}

export default EmployeeCardPage
